import React, { FC, useState, SyntheticEvent } from 'react';
import { MAIN_SITE_LINK } from '../../../constants';
import {
    SearchSuggestionsTownsAndCitiesWrapper,
    TownsAndCitiesHeadlineWrapper,
    SectionWrapper,
    Section,
    LinkWrapper,
    SectionIcon,
    IconAndHeadingWrapper,
    SectionHeading,
    Divider,
    StyledButtonLinkWithIcon,
    StyledLink,
    TabButtons,
    TabButton,
    TabContent,
    ContentContainer,
    MobileLink,
    DesktopLink,
    StyledHeading,
    StyledArrowsWrapperMobile,
    StyledArrowsWrapperTablet,
    StyledImage
} from './SearchSuggestionsTownsAndCities.styles';
import {
    trackBrowseAllCitiesTownsUK,
    trackSeeAllUKForSale,
    trackSeeAllUKToRent,
    trackIndividualLocationForSale,
    trackIndividualLocationToRent
} from './SearchSuggestionsTownsAndCities.tracking';
import { getTranslator } from '../../../shared/i18n/getTranslator';
import homepageTranslations from 'public/locales/en/homepage.json';
import { ButtonIconOnly, Icon, Text } from '@blueprint/ui';
import { Heading3 } from '@blueprint/heading';
import { SearchSuggestionsTownsAndCitiesProps } from './SearchSuggestionsTownsAndCities.types';
import CityIcon from '../../../../public/Icons/City_icon.png';
import CoastIcon from '../../../../public/Icons/Coast_icon.png';
import CountrysideIcon from '../../../../public/Icons/Countryside_icon.png';
import PopularLocationsIcon from '../../../../public/Icons/Popular_locations_icon.png';
import { ArrowRightMedium, ArrowLeftMedium } from '@blueprint/icons';

const t = getTranslator(homepageTranslations);

const ForSaleLabel = t('search_tabs.buy');
const ToRentLabel = t('search_tabs.rent');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateCity = (area: any, index: number) => {
    const { items } = area;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const translatedItems = items.map((item: any, innerIndex: number) => {
        return {
            ...item,
            headline: t(
                `towns_cities.areas[${index}].items[${innerIndex}].headline`
            )
        };
    });

    return {
        headline: t(`towns_cities.areas[${index}].headline`),
        sub_headline: t(`towns_cities.areas[${index}].sub_headline`),
        items: translatedItems
    };
};

const translateTownsAndCities = () => {
    const { areas } = homepageTranslations['towns_cities'];

    return {
        support_text_for_sale: t('towns_cities.support_text_for_sale'),
        support_text_to_rent: t('towns_cities.support_text_to_rent'),
        headline: t('towns_cities.headline'),
        cta: t('towns_cities.cta'),
        cta_UK_to_rent: t('towns_cities.cta_UK_to_rent'),
        cta_UK_for_sale: t('towns_cities.cta_UK_for_sale'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        areas: areas.map((area: any, index: number) =>
            translateCity(area, index)
        )
    };
};
const translatedTownsAndCities = translateTownsAndCities();

const sectionIcon = (headline: string) => {
    if (headline === 'In the city') {
        return CityIcon;
    } else if (headline === 'On the coast') {
        return CoastIcon;
    } else if (headline === 'Rural and countryside') {
        return CountrysideIcon;
    } else {
        return PopularLocationsIcon;
    }
};

enum Tab {
    ForSale = 'for-sale-tab',
    ToRent = 'to-rent-tab'
}

export const SearchSuggestionsTownsAndCities: FC<SearchSuggestionsTownsAndCitiesProps> = () => {
    const [selectedTabId, setSelectedTabId] = useState(Tab.ForSale);
    const [carouselIndex, setCarouselIndex] = useState(0);

    const isShowingForSale = selectedTabId === Tab.ForSale;
    const isShowingToRent = selectedTabId === Tab.ToRent;

    const decrementCarouselIndex = () => {
        setCarouselIndex((previousIndex) => previousIndex - 1);
    };

    const incrementCarouselIndex = () => {
        setCarouselIndex((previousIndex) => previousIndex + 1);
    };

    const sitemapLink = `${MAIN_SITE_LINK}/sitemap/`;
    const forSaleLink = `${MAIN_SITE_LINK}/for-sale/property/uk/`;
    const toRentLink = `${MAIN_SITE_LINK}/to-rent/property/uk/`;

    return (
        <SearchSuggestionsTownsAndCitiesWrapper>
            <TownsAndCitiesHeadlineWrapper>
                <StyledHeading size="5">
                    {translatedTownsAndCities.headline}
                </StyledHeading>
            </TownsAndCitiesHeadlineWrapper>
            <ContentContainer>
                <TabButtons role="tablist">
                    <TabButton
                        href="/for-sale/"
                        role="tab"
                        aria-selected={isShowingForSale}
                        aria-controls="suggestions-content-for-sale"
                        id="suggestions-tab-for-sale"
                        isActive={isShowingForSale}
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            setSelectedTabId(Tab.ForSale);
                        }}
                        data-testid="for-sale-tab"
                    >
                        {ForSaleLabel}
                    </TabButton>
                    <TabButton
                        role="tab"
                        aria-selected={isShowingToRent}
                        aria-controls="suggestions-content-to-rent"
                        id="suggestions-tab-to-rent"
                        isActive={isShowingToRent}
                        data-testid="to-rent-tab"
                        href="/to-rent/"
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            setSelectedTabId(Tab.ToRent);
                        }}
                    >
                        {ToRentLabel}
                    </TabButton>
                </TabButtons>

                <DesktopLink
                    href={sitemapLink}
                    variant="tertiary"
                    data-testid="explore_more-cta-desktop"
                    onClick={() => trackBrowseAllCitiesTownsUK}
                >
                    {translatedTownsAndCities.cta}
                </DesktopLink>

                <StyledArrowsWrapperMobile>
                    <ButtonIconOnly
                        disabled={carouselIndex === 0}
                        aria-label="Arrow left"
                        variant="tertiary"
                        iconOnly={true}
                        data-testid={`arrow_full_left_mobile`}
                        onClick={decrementCarouselIndex}
                    >
                        <ArrowLeftMedium />
                    </ButtonIconOnly>
                    <ButtonIconOnly
                        disabled={carouselIndex === 3}
                        aria-label="Arrow right"
                        variant="tertiary"
                        iconOnly={true}
                        data-testid={`arrow_full_right_mobile`}
                        onClick={incrementCarouselIndex}
                    >
                        <ArrowRightMedium />
                    </ButtonIconOnly>
                </StyledArrowsWrapperMobile>

                <StyledArrowsWrapperTablet>
                    <ButtonIconOnly
                        disabled={carouselIndex === 0}
                        aria-label="Arrow left"
                        variant="tertiary"
                        iconOnly={true}
                        data-testid={`arrow_full_left_tablet`}
                        onClick={decrementCarouselIndex}
                    >
                        <ArrowLeftMedium />
                    </ButtonIconOnly>
                    <ButtonIconOnly
                        disabled={carouselIndex === 1}
                        aria-label="Arrow right"
                        variant="tertiary"
                        iconOnly={true}
                        data-testid={`arrow_full_right_tablet`}
                        onClick={incrementCarouselIndex}
                    >
                        <ArrowRightMedium />
                    </ButtonIconOnly>
                </StyledArrowsWrapperTablet>

                <TabContent
                    role="tabpanel"
                    aria-labelledby="suggestions-tab-for-sale"
                    id="suggestions-content-for-sale"
                    isVisible={isShowingForSale}
                    carouselIndex={carouselIndex}
                >
                    {translatedTownsAndCities.areas.map(
                        (area, index: number) => {
                            const forSale = area.items.filter(
                                (item: { type: string }) =>
                                    item.type === 'for-sale'
                            );
                            return (
                                <>
                                    <SectionWrapper
                                        key={`towns-and-cities-for-sale-${index}`}
                                    >
                                        <SectionHeading>
                                            <SectionIcon>
                                                <StyledImage
                                                    src={sectionIcon(
                                                        area.headline
                                                    )}
                                                    alt=""
                                                />
                                            </SectionIcon>
                                            <IconAndHeadingWrapper>
                                                <Heading3 size="5">
                                                    {area.headline}
                                                </Heading3>
                                                <Text size="8">
                                                    {area.sub_headline}
                                                </Text>
                                                {area.headline ===
                                                'Popular locations' ? (
                                                    <StyledLink
                                                        href={forSaleLink}
                                                        variant="tertiary"
                                                        data-testid="see_all_UK_ForSale"
                                                        onClick={() =>
                                                            trackSeeAllUKForSale()
                                                        }
                                                    >
                                                        {
                                                            translatedTownsAndCities.cta_UK_for_sale
                                                        }
                                                    </StyledLink>
                                                ) : null}
                                            </IconAndHeadingWrapper>
                                        </SectionHeading>
                                        <Section
                                            key={`towns-and-cities-for-sale-first-${index}`}
                                        >
                                            {forSale
                                                .slice(0, 3)
                                                .map(
                                                    (
                                                        item: {
                                                            headline: string;
                                                            url: string;
                                                        },
                                                        index: number
                                                    ) => (
                                                        <LinkWrapper
                                                            key={index}
                                                        >
                                                            <StyledButtonLinkWithIcon
                                                                variant="tertiary"
                                                                data-testid={`city-living-link-${item.headline}`}
                                                                href={item.url}
                                                                onClick={() =>
                                                                    trackIndividualLocationForSale(
                                                                        item.headline
                                                                    )
                                                                }
                                                            >
                                                                <Icon
                                                                    iconId="arrow_full_right"
                                                                    size="small"
                                                                />
                                                                {item.headline}{' '}
                                                                {
                                                                    translatedTownsAndCities.support_text_for_sale
                                                                }
                                                            </StyledButtonLinkWithIcon>
                                                        </LinkWrapper>
                                                    )
                                                )}
                                        </Section>

                                        <Section
                                            key={`towns-and-cities-for-sale-second-${index}`}
                                        >
                                            {forSale
                                                .slice(3, 6)
                                                .map(
                                                    (
                                                        item: {
                                                            headline: string;
                                                            url: string;
                                                        },
                                                        index: number
                                                    ) => (
                                                        <LinkWrapper
                                                            key={index}
                                                        >
                                                            <StyledButtonLinkWithIcon
                                                                variant="tertiary"
                                                                data-testid={`city-living-link-${item.headline}`}
                                                                href={item.url}
                                                                onClick={() =>
                                                                    trackIndividualLocationForSale(
                                                                        item.headline
                                                                    )
                                                                }
                                                            >
                                                                <Icon
                                                                    iconId="arrow_full_right"
                                                                    size="small"
                                                                />
                                                                {item.headline}{' '}
                                                                {
                                                                    translatedTownsAndCities.support_text_for_sale
                                                                }
                                                            </StyledButtonLinkWithIcon>
                                                        </LinkWrapper>
                                                    )
                                                )}
                                        </Section>
                                    </SectionWrapper>
                                    {area.headline !== 'Popular locations' ? (
                                        <Divider />
                                    ) : null}
                                </>
                            );
                        }
                    )}
                </TabContent>

                <TabContent
                    role="tabpanel"
                    aria-labelledby="suggestions-tab-to-rent"
                    id="suggestions-content-to-rent"
                    isVisible={isShowingToRent}
                    carouselIndex={carouselIndex}
                >
                    {translatedTownsAndCities.areas.map(
                        (area, index: number) => {
                            const toRent = area.items.filter(
                                (i: { type: string }) => i.type === 'to-rent'
                            );
                            return (
                                <>
                                    <SectionWrapper
                                        key={`towns-and-cities-to-rent-${index}`}
                                    >
                                        <SectionHeading>
                                            <SectionIcon>
                                                <StyledImage
                                                    src={sectionIcon(
                                                        area.headline
                                                    )}
                                                    alt=""
                                                />
                                            </SectionIcon>
                                            <IconAndHeadingWrapper>
                                                <Heading3 size="5">
                                                    {area.headline}
                                                </Heading3>
                                                <Text size="8">
                                                    {area.sub_headline}
                                                </Text>
                                                {area.headline ===
                                                'Popular locations' ? (
                                                    <StyledLink
                                                        href={toRentLink}
                                                        variant="tertiary"
                                                        data-testid="see_all_UK_toRent"
                                                        onClick={() =>
                                                            trackSeeAllUKToRent()
                                                        }
                                                    >
                                                        {
                                                            translatedTownsAndCities.cta_UK_to_rent
                                                        }
                                                    </StyledLink>
                                                ) : null}
                                            </IconAndHeadingWrapper>
                                        </SectionHeading>
                                        <Section
                                            key={`towns-and-cities-to-rent-first-${index}`}
                                        >
                                            {toRent.slice(0, 3).map(
                                                (
                                                    item: {
                                                        headline: string;
                                                        url: string;
                                                    },
                                                    index: number
                                                ) => {
                                                    return (
                                                        <>
                                                            <LinkWrapper
                                                                key={index}
                                                            >
                                                                <StyledButtonLinkWithIcon
                                                                    variant="tertiary"
                                                                    data-testid={`city-living-link-${item.headline}`}
                                                                    href={
                                                                        item.url
                                                                    }
                                                                    onClick={() =>
                                                                        trackIndividualLocationToRent(
                                                                            item.headline
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        iconId="arrow_full_right"
                                                                        size="small"
                                                                    />
                                                                    {
                                                                        item.headline
                                                                    }{' '}
                                                                    {
                                                                        translatedTownsAndCities.support_text_to_rent
                                                                    }
                                                                </StyledButtonLinkWithIcon>
                                                            </LinkWrapper>
                                                        </>
                                                    );
                                                }
                                            )}
                                        </Section>
                                        <Section
                                            key={`towns-and-cities-to-rent-second-${index}`}
                                        >
                                            {toRent.slice(3, 6).map(
                                                (
                                                    item: {
                                                        headline: string;
                                                        url: string;
                                                    },
                                                    index: number
                                                ) => {
                                                    return (
                                                        <>
                                                            <LinkWrapper
                                                                key={index}
                                                            >
                                                                <StyledButtonLinkWithIcon
                                                                    variant="tertiary"
                                                                    data-testid={`city-living-link-${item.headline}`}
                                                                    href={
                                                                        item.url
                                                                    }
                                                                    onClick={() =>
                                                                        trackIndividualLocationToRent(
                                                                            item.headline
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        iconId="arrow_full_right"
                                                                        size="small"
                                                                    />
                                                                    {
                                                                        item.headline
                                                                    }{' '}
                                                                    {
                                                                        translatedTownsAndCities.support_text_to_rent
                                                                    }
                                                                </StyledButtonLinkWithIcon>
                                                            </LinkWrapper>
                                                        </>
                                                    );
                                                }
                                            )}
                                        </Section>
                                    </SectionWrapper>
                                    {area.headline !== 'Popular locations' ? (
                                        <Divider />
                                    ) : null}
                                </>
                            );
                        }
                    )}
                </TabContent>
            </ContentContainer>
            <MobileLink
                href={sitemapLink}
                variant="tertiary"
                data-testid="explore_more-cta-mobile"
                onClick={() => trackBrowseAllCitiesTownsUK()}
            >
                {translatedTownsAndCities.cta}
            </MobileLink>
        </SearchSuggestionsTownsAndCitiesWrapper>
    );
};
